import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useCreditsCheck } from '../hooks/useCreditsCheck';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import { ListPageSkeleton } from '../components/skeletons/ListPageSkeleton';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { Calendar, Mail, Send, Clock, CheckCircle, XCircle, Eye, Trash2, Plus, Users, CreditCard as Edit2, FileText, TestTube, Ban, AlertTriangle, Menu, Settings, LogOut } from 'lucide-react';
import { TestEmailModal } from '../components/TestEmailModal';
import { PageHeader } from '../components/design-system/PageHeader';
import { SearchBar } from '../components/design-system/SearchBar';
import { CampaignImagePlaceholder } from '../components/CampaignImagePlaceholder';
import { ScheduleCampaignModal } from '../components/ScheduleCampaignModal';
import { CampaignValidationErrorModal } from '../components/CampaignValidationErrorModal';
import { CampaignScheduledConfirmationModal } from '../components/CampaignScheduledConfirmationModal';
import { ConfirmCancelScheduledModal } from '../components/ConfirmCancelScheduledModal';
import { OverdueBadge, ScheduledDateDisplay, CampaignStatusBadge } from '../components/OverdueBadge';
import { ToastContainer } from '../components/Toast';
import { sendTestEmail } from '../lib/testEmailHelper';
import { validateCampaignScheduling, getSubscriberCount, ValidationError } from '../lib/campaignValidation';
import { getCampaignTimeStatus } from '../lib/campaignStatusHelper';

interface Campaign {
  id: string;
  subject: string;
  content?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'failed';
  scheduled_at: string | null;
  sent_at: string | null;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  blog_id: string | null;
  recipient_filters?: { tags?: string[] } | null;
  published_blogs?: {
    title: string;
    featured_image_url: string | null;
  };
}

type TabType = 'drafts' | 'sent' | 'scheduled';

export function EmailCampaigns() {
  const { user } = useAuth();
  const creditsInfo = useCreditsCheck(user?.id);
  const { isEnabled: aiGenerationEnabled } = useAIGenerationEnabled();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('drafts');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testCampaign, setTestCampaign] = useState<Campaign | null>(null);
  const [sendingTestId, setSendingTestId] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleCampaign, setScheduleCampaign] = useState<Campaign | null>(null);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [schedulingId, setSchedulingId] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; type: 'success' | 'error' | 'warning' | 'info'; title: string; message?: string; duration?: number }>>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScheduledConfirmation, setShowScheduledConfirmation] = useState(false);
  const [confirmedScheduledAt, setConfirmedScheduledAt] = useState<Date | null>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelCampaignId, setCancelCampaignId] = useState<string | null>(null);
  const [contactTags, setContactTags] = useState<string[]>([]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadCampaigns();
      loadContactTags();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  // Bestaande groepen voor de doelgroep-keuze in de inplan-modal.
  const loadContactTags = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('email_contacts')
        .select('tags')
        .eq('user_id', user.id)
        .eq('subscribed', true);

      if (error) {
        console.error('Error loading contact tags:', error);
        return;
      }

      const tags = Array.from(
        new Set((data || []).flatMap((row: { tags: string[] | null }) => row.tags || []))
      ).sort((a, b) => a.localeCompare(b, 'nl'));
      setContactTags(tags);
    } catch (err) {
      console.error('Error loading contact tags:', err);
    }
  };

  useEffect(() => {
    filterCampaigns();
  }, [activeTab, campaigns, searchQuery]);

  const loadCampaigns = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('id, subject, content, status, scheduled_at, sent_at, total_contacts, sent_count, failed_count, created_at, blog_id, recipient_filters, published_blogs(title, featured_image_url)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading campaigns:', error);
      } else {
        const normalized: Campaign[] = (data || []).map((c: any) => ({
          ...c,
          published_blogs: Array.isArray(c.published_blogs)
            ? c.published_blogs[0]
            : c.published_blogs,
        }));
        setCampaigns(normalized);
      }
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    let filtered = campaigns;

    switch (activeTab) {
      case 'drafts':
        filtered = campaigns.filter((c) => c.status === 'draft');
        break;
      case 'sent':
        filtered = campaigns.filter((c) => c.status === 'completed' || c.status === 'failed');
        break;
      case 'scheduled':
        filtered = campaigns.filter((c) => c.status === 'scheduled' || c.status === 'sending');
        filtered.sort((a, b) => {
          const aStatus = getCampaignTimeStatus(a.scheduled_at, a.status);
          const bStatus = getCampaignTimeStatus(b.scheduled_at, b.status);
          if (aStatus.isOverdue && !bStatus.isOverdue) return -1;
          if (!aStatus.isOverdue && bStatus.isOverdue) return 1;
          if (!a.scheduled_at || !b.scheduled_at) return 0;
          return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
        });
        break;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((c) =>
        c.subject.toLowerCase().includes(query) ||
        c.published_blogs?.title.toLowerCase().includes(query)
      );
    }

    setFilteredCampaigns(filtered);
  };

  const deleteCampaign = async (campaignId: string) => {
    if (!confirm('Weet je zeker dat je deze campaign wilt verwijderen?')) {
      return;
    }

    try {
      setDeletingId(campaignId);
      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) {
        console.error('Error deleting campaign:', error);
        alert('Er ging iets mis bij het verwijderen');
      } else {
        setCampaigns(campaigns.filter((c) => c.id !== campaignId));
      }
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Er ging iets mis bij het verwijderen');
    } finally {
      setDeletingId(null);
    }
  };

  const duplicateCampaign = async (campaign: Campaign) => {
    try {
      const { data: fullCampaign } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('id', campaign.id)
        .single();

      if (!fullCampaign) throw new Error('Campaign not found');

      const { data, error } = await supabase
        .from('email_campaigns')
        .insert({
          user_id: fullCampaign.user_id,
          blog_id: fullCampaign.blog_id,
          subject: `${fullCampaign.subject} (kopie)`,
          content: fullCampaign.content,
          from_name: fullCampaign.from_name,
          status: 'draft',
        })
        .select()
        .single();

      if (error) throw error;

      navigate(`/campaigns/edit/${data.id}`);
    } catch (err) {
      console.error('Error duplicating campaign:', err);
      alert('Er ging iets mis bij het dupliceren');
    }
  };

  const handleOpenTestModal = async (campaign: Campaign) => {
    try {
      setSendingTestId(campaign.id);
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('id, subject, content')
        .eq('id', campaign.id)
        .single();

      if (error) throw error;

      setTestCampaign(data as Campaign);
      setShowTestModal(true);
    } catch (err) {
      console.error('Error loading campaign:', err);
      alert('Er ging iets mis bij het laden van de campaign');
    } finally {
      setSendingTestId(null);
    }
  };

  const addToast = (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, type, title, message }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const handleSendTest = async (email: string, name: string) => {
    if (!user || !testCampaign) return;

    await sendTestEmail({
      userId: user.id,
      toEmail: email,
      toName: name,
      subject: testCampaign.subject || '',
      content: testCampaign.content || '',
    });

    addToast('success', 'Test email verzonden!', `De test email is succesvol verzonden naar ${email}`);
  };

  const handleScheduleClick = async (campaign: Campaign) => {
    if (!user) return;

    try {
      setSchedulingId(campaign.id);
      const validation = await validateCampaignScheduling(user.id, campaign.id);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setShowValidationErrorModal(true);
        return;
      }

      setScheduleCampaign(campaign);
      setSubscriberCount(validation.subscriberCount || 0);
      setShowScheduleModal(true);
    } catch (err) {
      console.error('Error validating campaign:', err);
      alert('Er ging iets mis bij het valideren van de campaign');
    } finally {
      setSchedulingId(null);
    }
  };

  const handleConfirmSchedule = async (scheduledAt: Date, filterTags: string[]) => {
    if (!scheduleCampaign || !user) return;

    try {
      const { error } = await supabase
        .from('email_campaigns')
        .update({
          status: 'scheduled',
          scheduled_at: scheduledAt.toISOString(),
          recipient_filters: filterTags.length > 0 ? { tags: filterTags } : null,
        })
        .eq('id', scheduleCampaign.id);

      if (error) throw error;

      await loadCampaigns();
      setActiveTab('scheduled');

      setConfirmedScheduledAt(scheduledAt);
      setShowScheduledConfirmation(true);
    } catch (err) {
      console.error('Error scheduling campaign:', err);
      addToast('error', 'Fout bij inplannen', 'Er ging iets mis bij het inplannen van de campaign');
    }
  };

  const cancelScheduledCampaign = (campaignId: string) => {
    setCancelCampaignId(campaignId);
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelCampaignId) return;

    try {
      setCancelingId(cancelCampaignId);
      const { error } = await supabase
        .from('email_campaigns')
        .update({
          status: 'draft',
          scheduled_at: null,
          recipient_filters: null,
        })
        .eq('id', cancelCampaignId)
        .eq('status', 'scheduled');

      if (error) throw error;

      await loadCampaigns();
      setActiveTab('drafts');
      setShowCancelConfirmation(false);
      setCancelCampaignId(null);
      addToast('success', 'Campaign geannuleerd en teruggezet naar concepten');
    } catch (err) {
      console.error('Error canceling campaign:', err);
      addToast('error', 'Fout bij annuleren', 'Er ging iets mis bij het annuleren van de campaign');
    } finally {
      setCancelingId(null);
    }
  };

  const getStatusBadge = (status: Campaign['status'], campaign: Campaign) => {
    const timeStatus = getCampaignTimeStatus(campaign.scheduled_at, campaign.status);

    const getScheduledStyle = () => {
      if (timeStatus.processingStatus === 'overdue') return 'bg-red-100 text-red-800 border-red-300';
      if (timeStatus.processingStatus === 'delayed') return 'bg-amber-50 text-amber-700 border-amber-300';
      if (timeStatus.processingStatus === 'processing') return 'bg-blue-50 text-blue-700 border-blue-200';
      return 'bg-blue-100 text-blue-800 border-blue-200';
    };

    const styles = {
      draft: 'bg-orange-100 text-orange-800 border-orange-200',
      scheduled: getScheduledStyle(),
      sending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
    };

    const getScheduledIcon = () => {
      if (timeStatus.processingStatus === 'overdue') return AlertTriangle;
      return Clock;
    };

    const icons = {
      draft: FileText,
      scheduled: getScheduledIcon(),
      sending: Send,
      completed: CheckCircle,
      failed: XCircle,
    };

    const Icon = icons[status];

    const getScheduledLabel = () => {
      if (timeStatus.processingStatus === 'overdue') return 'Te Laat';
      if (timeStatus.processingStatus === 'delayed') return 'Vertraagd';
      if (timeStatus.processingStatus === 'processing') return 'Wordt verwerkt';
      return 'Ingepland';
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
        <Icon className="w-4 h-4" />
        {status === 'draft' && 'Concept'}
        {status === 'scheduled' && getScheduledLabel()}
        {status === 'sending' && 'Bezig...'}
        {status === 'completed' && 'Verzonden'}
        {status === 'failed' && 'Mislukt'}
      </span>
    );
  };

  const getTabCount = (tab: TabType) => {
    switch (tab) {
      case 'drafts':
        return campaigns.filter((c) => c.status === 'draft').length;
      case 'sent':
        return campaigns.filter((c) => c.status === 'completed' || c.status === 'failed').length;
      case 'scheduled':
        return campaigns.filter((c) => c.status === 'scheduled' || c.status === 'sending').length;
    }
  };

  const getOverdueCount = () => {
    return campaigns.filter((c) => {
      if (c.status !== 'scheduled') return false;
      const timeStatus = getCampaignTimeStatus(c.scheduled_at, c.status);
      return timeStatus.processingStatus === 'overdue';
    }).length;
  };

  const getPendingProcessingCount = () => {
    return campaigns.filter((c) => {
      if (c.status !== 'scheduled') return false;
      const timeStatus = getCampaignTimeStatus(c.scheduled_at, c.status);
      return timeStatus.processingStatus === 'processing' || timeStatus.processingStatus === 'delayed';
    }).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <Sidebar onSignOut={handleSignOut} />
        <MobileNav
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onSignOut={handleSignOut}
        />
        <div className="lg:pl-64">
          <MaintenanceBanner />
          <LowCreditsBanner />
          <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Menu"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  <img
                    src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                    alt="Dibbi Logo"
                    className="h-8 lg:hidden"
                  />
                </div>
                <div className="flex items-center gap-3 lg:hidden">
                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Instellingen"
                  >
                    <Settings className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Uitloggen"
                  >
                    <LogOut className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </header>
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
            <ListPageSkeleton rows={5} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
        <div className="space-y-6">
          <PageHeader
            title="Email campagnes"
            subtitle="Verstuur je blogs naar je email lijst"
            actions={
              <>
                <button
                  onClick={() => navigate('/mailinglist')}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  <span className="hidden sm:inline">Beheer contacten</span>
                </button>
                <button
                  onClick={() => navigate('/campaigns/create')}
                  disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                  title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.` : ''}
                  className={`btn-primary inline-flex items-center gap-2 ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden sm:inline">Nieuwe campagne</span>
                </button>
              </>
            }
          />

          {getOverdueCount() > 0 && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-900 mb-1">
                  Je hebt {getOverdueCount()} campaign{getOverdueCount() > 1 ? 's' : ''} die te laat {getOverdueCount() > 1 ? 'zijn' : 'is'}
                </p>
                <p className="text-sm text-red-800">
                  Deze campaigns stonden ingepland in het verleden maar zijn nog niet verstuurd. Controleer je credits of annuleer ze indien nodig.
                </p>
              </div>
            </div>
          )}

          {!creditsInfo.hasCredits && (
            <div className="card bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900 mb-1">
                    Onvoldoende credits om campaigns te versturen
                  </p>
                  <p className="text-sm text-red-800 mb-3">
                    Je hebt {creditsInfo.total} credits. Je kunt geen nieuwe campaigns maken of versturen tot je credits zijn bijgevuld.
                    {creditsInfo.nextBillingDate && (
                      <span className="block mt-1">
                        Volgende automatische bijvulling: <span className="font-semibold">{new Date(creditsInfo.nextBillingDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </span>
                    )}
                  </p>
                  <a
                    href="https://checkout.dibbi.nl/checkout/credits"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Credits Aankopen
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="border-b border-gray-200">
            <nav className="flex gap-8">
              <button
                onClick={() => setActiveTab('drafts')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                  activeTab === 'drafts'
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Concepten
                  {getTabCount('drafts') > 0 && (
                    <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {getTabCount('drafts')}
                    </span>
                  )}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('scheduled')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                  activeTab === 'scheduled'
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Ingepland
                  {getTabCount('scheduled') > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {getTabCount('scheduled')}
                    </span>
                  )}
                  {getOverdueCount() > 0 && (
                    <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-0.5 rounded-full border border-red-300">
                      {getOverdueCount()} TE LAAT
                    </span>
                  )}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('sent')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                  activeTab === 'sent'
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Verzonden
                  {getTabCount('sent') > 0 && (
                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {getTabCount('sent')}
                    </span>
                  )}
                </span>
              </button>
            </nav>
          </div>

          {campaigns.length > 3 && (
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Zoek campagne..."
            />
          )}

          {filteredCampaigns.length === 0 ? (
            <div className="card text-center py-12">
              {activeTab === 'drafts' && (
                <>
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-xl text-gray-600 mb-4">
                    Geen concept campagnes
                  </p>
                  <p className="text-gray-500 mb-6">
                    Maak je eerste campagne om je blog te delen met je contacten
                  </p>
                  <button
                    onClick={() => navigate('/campaigns/create')}
                    disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                    title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits, minimum is ${creditsInfo.negativeLimit}.` : ''}
                    className={`btn-primary inline-flex items-center gap-2 ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Plus className="w-5 h-5" />
                    Maak je eerste campagne
                  </button>
                </>
              )}
              {activeTab === 'scheduled' && (
                <>
                  <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-xl text-gray-600 mb-4">
                    Geen ingeplande campagnes
                  </p>
                  <p className="text-gray-500">
                    Campagnes die je inplant verschijnen hier
                  </p>
                </>
              )}
              {activeTab === 'sent' && (
                <>
                  <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-xl text-gray-600 mb-4">
                    Nog geen verzonden campagnes
                  </p>
                  <p className="text-gray-500">
                    Verzonden campagnes verschijnen hier met statistieken
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign) => (
                <div key={campaign.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex flex-col md:flex-row gap-6">
                    {campaign.published_blogs?.featured_image_url ? (
                      <img
                        src={campaign.published_blogs.featured_image_url}
                        alt={campaign.subject}
                        className="w-full md:w-32 h-32 object-cover rounded-lg"
                      />
                    ) : (
                      <CampaignImagePlaceholder />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-xl font-bold text-[#231F20] truncate">
                              {campaign.subject}
                            </h3>
                            {getStatusBadge(campaign.status, campaign)}
                          </div>

                          {campaign.published_blogs && (
                            <p className="text-sm text-gray-500 mb-3">
                              Blog: {campaign.published_blogs.title}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
                        {campaign.scheduled_at && campaign.status === 'scheduled' && (() => {
                          const timeStatus = getCampaignTimeStatus(campaign.scheduled_at, campaign.status);
                          return (
                            <ScheduledDateDisplay
                              scheduledAt={campaign.scheduled_at}
                              isOverdue={timeStatus.isOverdue}
                              processingStatus={timeStatus.processingStatus}
                              statusLabel={timeStatus.statusLabel}
                              formattedLateness={timeStatus.formattedLateness}
                            />
                          );
                        })()}

                        {campaign.sent_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Verzonden: {new Date(campaign.sent_at).toLocaleString('nl-NL')}
                            </span>
                          </div>
                        )}

                        {campaign.total_contacts > 0 && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            <span>
                              {campaign.sent_count} / {campaign.total_contacts} verzonden
                            </span>
                          </div>
                        )}

                        {(campaign.recipient_filters?.tags?.length ?? 0) > 0 && (
                          <div className="flex items-center gap-1 text-[#B84A1F]">
                            <Users className="w-4 h-4" />
                            <span>
                              Groep: {campaign.recipient_filters!.tags!.join(', ')}
                            </span>
                          </div>
                        )}

                        {campaign.failed_count > 0 && (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-4 h-4" />
                            <span>{campaign.failed_count} mislukt</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {campaign.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleScheduleClick(campaign)}
                              disabled={schedulingId === campaign.id || !creditsInfo.hasCredits}
                              title={!creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits.` : ''}
                              className={`btn-primary inline-flex items-center gap-2 text-sm ${!creditsInfo.hasCredits ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <Send className="w-4 h-4" />
                              {schedulingId === campaign.id ? 'Valideren...' : 'Inplannen'}
                            </button>

                            <button
                              onClick={() => navigate(`/campaigns/edit/${campaign.id}`)}
                              disabled={!creditsInfo.hasCredits}
                              title={!creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits.` : ''}
                              className={`btn-secondary inline-flex items-center gap-2 text-sm ${!creditsInfo.hasCredits ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <Edit2 className="w-4 h-4" />
                              Bewerken
                            </button>

                            {(() => {
                              const hasSubject = !!campaign.subject?.trim();
                              const hasContent = !!campaign.content?.trim();
                              const canTest = hasSubject && hasContent;
                              const tip = !canTest
                                ? 'Vul subject en content in om een test email te versturen'
                                : '';
                              return (
                                <button
                                  onClick={() => handleOpenTestModal(campaign)}
                                  disabled={sendingTestId === campaign.id || !canTest}
                                  title={tip}
                                  className={`btn-secondary inline-flex items-center gap-2 text-sm ${!canTest ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <TestTube className="w-4 h-4" />
                                  {sendingTestId === campaign.id ? 'Laden...' : 'Test Versturen'}
                                </button>
                              );
                            })()}

                            <button
                              onClick={() => duplicateCampaign(campaign)}
                              disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                              title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits.` : ''}
                              className={`btn-secondary inline-flex items-center gap-2 text-sm ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <FileText className="w-4 h-4" />
                              Dupliceren
                            </button>
                          </>
                        )}

                        {campaign.status === 'scheduled' && (
                          <>
                            <button
                              onClick={() => cancelScheduledCampaign(campaign.id)}
                              disabled={cancelingId === campaign.id}
                              className="btn-primary inline-flex items-center gap-2 text-sm bg-yellow-600 hover:bg-yellow-700 border-yellow-600 hover:border-yellow-700"
                            >
                              <Ban className="w-4 h-4" />
                              {cancelingId === campaign.id ? 'Annuleren...' : 'Annuleer Planning'}
                            </button>

                            <button
                              onClick={() => navigate(`/campaigns/${campaign.id}`)}
                              className="btn-secondary inline-flex items-center gap-2 text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              Details
                            </button>

                            <button
                              onClick={() => duplicateCampaign(campaign)}
                              disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                              title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits.` : ''}
                              className={`btn-secondary inline-flex items-center gap-2 text-sm ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <FileText className="w-4 h-4" />
                              Dupliceren
                            </button>
                          </>
                        )}

                        {(campaign.status === 'completed' || campaign.status === 'failed' || campaign.status === 'sending') && (
                          <>
                            <button
                              onClick={() => navigate(`/campaigns/${campaign.id}`)}
                              className="btn-secondary inline-flex items-center gap-2 text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              Details
                            </button>

                            <button
                              onClick={() => duplicateCampaign(campaign)}
                              disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                              title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? `Onvoldoende credits. Je hebt ${creditsInfo.total} credits.` : ''}
                              className={`btn-secondary inline-flex items-center gap-2 text-sm ${(!creditsInfo.hasCredits || !aiGenerationEnabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <FileText className="w-4 h-4" />
                              Dupliceren
                            </button>
                          </>
                        )}

                        {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                          <button
                            onClick={() => deleteCampaign(campaign.id)}
                            disabled={deletingId === campaign.id}
                            className="btn-secondary inline-flex items-center gap-2 text-sm hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            {deletingId === campaign.id ? 'Verwijderen...' : 'Verwijder'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <TestEmailModal
          isOpen={showTestModal}
          onClose={() => {
            setShowTestModal(false);
            setTestCampaign(null);
          }}
          onSend={handleSendTest}
          defaultEmail={user?.email || ''}
          defaultName={user?.first_name || ''}
        />

        <ScheduleCampaignModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setScheduleCampaign(null);
          }}
          onConfirm={handleConfirmSchedule}
          subscriberCount={subscriberCount}
          campaignSubject={scheduleCampaign?.subject || ''}
          availableTags={contactTags}
          onCountForTags={user ? (tags) => getSubscriberCount(user.id, tags) : undefined}
        />

        <CampaignValidationErrorModal
          isOpen={showValidationErrorModal}
          onClose={() => setShowValidationErrorModal(false)}
          errors={validationErrors}
        />

        <CampaignScheduledConfirmationModal
          isOpen={showScheduledConfirmation}
          onClose={() => {
            setShowScheduledConfirmation(false);
            setConfirmedScheduledAt(null);
          }}
          scheduledAt={confirmedScheduledAt || new Date()}
        />

        <ConfirmCancelScheduledModal
          isOpen={showCancelConfirmation}
          onClose={() => {
            setShowCancelConfirmation(false);
            setCancelCampaignId(null);
          }}
          onConfirm={handleConfirmCancel}
          isLoading={cancelingId === cancelCampaignId}
        />

        <ToastContainer toasts={toasts} onClose={removeToast} />
      </main>
      </div>
    </div>
  );
}
